import React, { useMemo } from 'react';

/**
 * Renders an AI fix (Markdown from the model) as formatted text.
 *
 * WB-30 (re-walk 30 Sep 2026): the fix was printed raw in a pre-wrap div, so
 * customers saw "# SEO Issue Fix", "**bold**" and code fences literally.
 *
 * The dashboard has no Markdown library (react-markdown is a WebUi dependency
 * only), and this must not add one. So this is a small renderer for the subset
 * the model writes: headings, bullet and numbered lists, code fences, tables,
 * rules, paragraphs, and inline bold / italic / code / links.
 *
 * SAFETY: it builds React elements only. No HTML string is ever produced and
 * nothing goes through dangerouslySetInnerHTML, so any HTML in the model's
 * output (a <script>, an <img onerror>) is shown as literal text by React's
 * escaping. There is nothing for sanitizeHTML to clean. Links become anchors
 * only for http(s) URLs; anything else stays plain text.
 *
 * PERFORMANCE (review of WB-30): every pattern here must run in linear time on
 * a single line, because the text is model output cached for 24 hours and a
 * catastrophic-backtracking pattern would freeze the tab on every open. The
 * heading pattern used to be `(.*?)\s*#*\s*$`, which was cubic on a heading
 * followed by a long run of spaces (4,000 spaces took 13.6 s). Closing hashes
 * are now stripped with plain string steps. Text above MAX_RENDERED_CHARS is
 * shown as plain text without parsing.
 */

/** Above this many characters the fix is shown as plain text, unparsed. */
export const MAX_RENDERED_CHARS = 20_000;

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[]; start: number }
  | { kind: 'code'; text: string }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'rule' }
  | { kind: 'para'; lines: string[] };

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s{0,3}(#{1,6})[ \t]+(.*)$/;
const BULLET = /^\s*[-*+•]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

/**
 * A heading's text without the optional closing hashes ("## Title ##"). Only a
 * run of hashes after a space or tab counts, so "Use C#" keeps its "#".
 * Linear: trimEnd plus one end-anchored replace whose `#+` can only start after
 * a space.
 */
function headingText(raw: string): string {
  return raw.trimEnd().replace(/(^|[ \t])#+$/, '').trimEnd();
}

/** The "|---|:---:|" line under a table header. */
function isTableSeparator(line: string): boolean {
  return splitRow(line).every((cell) => /^:?-{2,}:?$/.test(cell));
}

export function parseAiFixMarkdown(source: string): Block[] {
  const lines = String(source ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (FENCE.test(line)) {
      const fence = line.trim().slice(0, 3);
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith(fence)) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence (or end of input)
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: headingText(heading[2]) });
      i += 1;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }

    if (TABLE_ROW.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        if (!isTableSeparator(lines[i])) rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ kind: 'table', rows });
      continue;
    }

    if (BULLET.test(line)) {
      const items: string[] = [];
      while (i < lines.length && BULLET.test(lines[i])) {
        items.push((BULLET.exec(lines[i]) as RegExpExecArray)[1]);
        i += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    const numbered = NUMBERED.exec(line);
    if (numbered) {
      const items: string[] = [];
      const start = Number(numbered[1]) || 1;
      while (i < lines.length && NUMBERED.test(lines[i])) {
        items.push((NUMBERED.exec(lines[i]) as RegExpExecArray)[2]);
        i += 1;
      }
      blocks.push({ kind: 'ol', items, start });
      continue;
    }

    // Paragraph: consecutive lines that start no other block.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !FENCE.test(lines[i]) &&
      !HEADING.test(lines[i]) &&
      !RULE.test(lines[i]) &&
      !TABLE_ROW.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i])
    ) {
      const quote = QUOTE.exec(lines[i]);
      para.push(quote ? quote[1] : lines[i]);
      i += 1;
    }
    blocks.push({ kind: 'para', lines: para });
  }

  return blocks;
}

// `code` | **bold** | __bold__ | [text](url) | *italic*
// Each body excludes its own opener as well as its closer, so a scan from one
// opener stops at the next one: a long run of "[" or "*" without a closer stays
// linear instead of rescanning to the end of the line from every opener.
const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\[[^[\]\n]+\]\([^()\s]+\))|(\*[^*\s\n][^*\n]*\*)/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last) out.push(text.slice(last, index));
    const token = match[0];
    const key = `${keyPrefix}-${n++}`;
    if (match[1]) {
      out.push(
        <code key={key} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-[0.85em]">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (match[2] || match[3]) {
      out.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
    } else if (match[4]) {
      const linkMatch = /^\[([^[\]]+)\]\(([^()\s]+)\)$/.exec(token) as RegExpExecArray;
      const label = linkMatch[1];
      const href = linkMatch[2];
      out.push(
        /^https?:\/\//i.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline text-purple-700 dark:text-purple-300">
            {label}
          </a>
        ) : (
          <React.Fragment key={key}>{label}</React.Fragment>
        ),
      );
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderBlocks(blocks: Block[]): React.ReactNode[] {
  return blocks.map((block, b) => {
    const key = `b${b}`;
    switch (block.kind) {
      case 'heading':
        return block.level <= 2 ? (
          <h5 key={key} className="text-base font-semibold text-gray-900 dark:text-white">
            {renderInline(block.text, key)}
          </h5>
        ) : (
          <h6 key={key} className="text-sm font-semibold text-gray-900 dark:text-white">
            {renderInline(block.text, key)}
          </h6>
        );
      case 'ul':
        return (
          <ul key={key} className="list-disc pl-5 space-y-1">
            {block.items.map((item, i) => (
              <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
            ))}
          </ul>
        );
      case 'ol':
        return (
          <ol key={key} start={block.start} className="list-decimal pl-5 space-y-1">
            {block.items.map((item, i) => (
              <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
            ))}
          </ol>
        );
      case 'code':
        return (
          <pre
            key={key}
            className="whitespace-pre-wrap font-mono text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto"
          >
            <code>{block.text}</code>
          </pre>
        );
      case 'table':
        return (
          <div key={key} className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={`${key}-${r}`} className={r === 0 ? 'font-semibold' : undefined}>
                    {row.map((cell, c) => (
                      <td key={`${key}-${r}-${c}`} className="border border-gray-200 dark:border-gray-700 px-2 py-1 align-top">
                        {renderInline(cell, `${key}-${r}-${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'rule':
        return <hr key={key} className="border-gray-200 dark:border-gray-700" />;
      case 'para':
      default:
        return (
          <p key={key}>
            {block.lines.map((line, l) => (
              <React.Fragment key={`${key}-${l}`}>
                {l > 0 && <br />}
                {renderInline(line, `${key}-${l}`)}
              </React.Fragment>
            ))}
          </p>
        );
    }
  });
}

/** Inline markup removed, content kept: `code`, **bold**, __bold__, [label](url), *italic*. */
function stripInline(text: string): string {
  // String.replace collects every match before calling the replacer, so the
  // recursive call below can reuse INLINE. A bold body cannot hold "*", so the
  // recursion is at most a couple of levels deep.
  return text.replace(INLINE, (token: string, code?: string, bold?: string, boldUnderscore?: string, link?: string) => {
    if (code) return token.slice(1, -1);
    if (bold || boldUnderscore) return stripInline(token.slice(2, -2));
    if (link) return token.slice(1, token.indexOf(']('));
    return token.slice(1, -1);
  });
}

/** Lines joined into one run: a space after sentence punctuation, " · " otherwise. */
function joinRun(parts: string[]): string {
  let out = '';
  for (const raw of parts) {
    const part = raw.trim();
    if (!part) continue;
    if (!out) {
      out = part;
      continue;
    }
    out += '.!?:;,'.includes(out[out.length - 1]) ? ` ${part}` : ` · ${part}`;
  }
  return out;
}

/**
 * The same Markdown as plain text, for places that show a short excerpt (a card
 * with a two-line clamp) rather than the formatted body. WB-43: Autopilot cards
 * printed "**Page:** /local-seo-bath **LCP Time:** 21628ms" literally.
 * Linear time, like the parser; above MAX_RENDERED_CHARS the text is only
 * whitespace-collapsed.
 */
export function markdownToPlainText(source: string | null | undefined): string {
  const text = String(source ?? '');
  if (!text) return '';
  if (text.length > MAX_RENDERED_CHARS) return text.replace(/\s+/g, ' ').trim();
  const parts: string[] = [];
  for (const block of parseAiFixMarkdown(text)) {
    switch (block.kind) {
      case 'heading':
        parts.push(stripInline(block.text));
        break;
      case 'ul':
      case 'ol':
        parts.push(joinRun(block.items.map(stripInline)));
        break;
      case 'code':
        parts.push(block.text);
        break;
      case 'table':
        parts.push(joinRun(block.rows.map((row) => joinRun(row.map(stripInline)))));
        break;
      case 'para':
        parts.push(joinRun(block.lines.map(stripInline)));
        break;
      case 'rule':
      default:
        break;
    }
  }
  return parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const AiFixMarkdown: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  // Parsed and rendered once per text, not on every parent render (the /issues
  // and SEO Analysis panels re-render on unrelated state changes).
  const rendered = useMemo(
    () => (text.length > MAX_RENDERED_CHARS ? null : renderBlocks(parseAiFixMarkdown(text))),
    [text],
  );
  if (rendered === null) {
    // Too long to format safely: show it as plain text instead.
    return (
      <div className={`whitespace-pre-wrap break-words ${className ?? ''}`} data-testid="ai-fix-plain">
        {text}
      </div>
    );
  }
  return (
    <div className={`space-y-2 break-words ${className ?? ''}`} data-testid="ai-fix-markdown">
      {rendered}
    </div>
  );
};

export default React.memo(AiFixMarkdown);
